import { HDMPriceDisplay } from '@/components/blocks/HDMPriceDisplay';
import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import { NumberInput } from '@/components/blocks/NumberInput';
import { useOnTrackSelectItem } from '@/components/blocks/PLPProductCard/hooks/useOnTrackSelectItem';
import { AddProductCart } from '@/components/blocks/ProductDetails/parts/AddProductCart';
import { MAX_QTY } from '@/components/content/MerchandisingAssociation/parts/constants';
import { LayoutProps } from '@/components/content/MerchandisingAssociation/parts/LayoutProps';
import {
	cardBuyTogetherText,
	cardFirstStyle,
	cardTotalPriceContainerSx,
	cartDesktopImageSx,
	getRootStyles,
	styles,
} from '@/components/content/MerchandisingAssociation/styles/style';
import { useCart } from '@/data/Content/Cart';
import { useLocalization } from '@/data/Localization';
import { OrderItem } from '@/data/types/Order';
import { ProductType } from '@/data/types/Product';
import { textTransformToSequence } from '@/utils/commons';
import { getHref_Product } from '@/utils/getHref_Product';
import { parseHTML } from '@/utils/parseHTMLModern';
import AddIcon from '@mui/icons-material/Add';
import {
	Box,
	Card,
	CardContent,
	Checkbox,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CDN_BASE_URL } from 'shared/custom/constants/commonConstants';
import { useHomeDeliveryDate } from '@/data/Content/HomeDeliveryDate';
import { usePickupDate } from '@/data/Content/PickupDate';
import { getStoreIdAndStateId } from 'shared/custom/utils/cookieHandler';

interface ProductCardProps {
	product: ProductType;
	itemKey: string;
	isChecked: boolean;
	quantity: number;
	isMobile: boolean;
	onToggle: (id: string, next?: boolean) => void;
	onQtySet?: (id: string, value: number) => void;
	slidesQty: number;
	orderItems: OrderItem[];
}

const ProductCard: React.FC<ProductCardProps> = memo(
	({
		product,
		itemKey,
		isChecked,
		quantity,
		isMobile,
		onToggle,
		onQtySet,
		slidesQty,
		orderItems,
	}) => {
		const { id, name, productPrice, partNumber } = product;
		const nominalQuantity = Number(product?.['x_measurements.nominalQuantity'] ?? '1');
		const routeUrl = useMemo(() => getHref_Product(product, []), [product]);
		const [nominalQty, setNominalQty] = useState<number>(nominalQuantity);
		const onQuantityChange = useCallback(
			(quantity: number | null) => onQtySet && onQtySet(id, quantity ?? 1),
			[id, onQtySet]
		);
		const onTrackSelectItem = useOnTrackSelectItem({ product });

		useEffect(() => {
			if (nominalQuantity === 1) return;
			const productInCart = orderItems?.find((orderItem) => orderItem.partNumber === partNumber);
			const newNominalQuantity = productInCart
				? nominalQuantity - parseInt(productInCart.quantity) <= 0
					? 1
					: nominalQuantity
				: nominalQuantity;

			setNominalQty(newNominalQuantity);
			onQuantityChange(newNominalQuantity);
		}, [orderItems, nominalQuantity, partNumber, onQuantityChange]);

		return (
			<CardContent sx={styles.cardLoopItemContainer}>
				{isMobile ? (
					<Box sx={styles.cardMobileHeaderContainer}>
						<Box sx={styles.cardMobileHeader}>
							<Box sx={styles.cardMobileImage}>
								<Linkable href={routeUrl} color="textPrimary" onClick={onTrackSelectItem}>
									<Img src={`${CDN_BASE_URL}/productos/${itemKey}/${itemKey}-m.jpg`} alt={name} />
								</Linkable>
							</Box>
							<Box sx={styles.cardMobileName}>
								<Linkable href={routeUrl} color="textPrimary" onClick={onTrackSelectItem}>
									<Typography
										className="buy-together__product-name"
										variant="bodyBaseline"
										sx={styles.cardNameDescriptionValue}
									>
										{name && parseHTML(textTransformToSequence(name))}
									</Typography>
								</Linkable>
								<Box className="buy-together__product-price" sx={styles.cardControlContainer}>
									<HDMPriceDisplay price={productPrice} variant="sm" />
								</Box>
							</Box>
						</Box>
						<Checkbox
							checked={isChecked}
							onChange={(e) => {
								const isChecked = e.target.checked;
								onToggle(id, isChecked);
							}}
							sx={styles.cardCheckboxElement}
						/>
					</Box>
				) : (
					<>
						<Box sx={cartDesktopImageSx(slidesQty)}>
							<Linkable href={routeUrl} color="textPrimary" onClick={onTrackSelectItem}>
								<Img
									src={`${CDN_BASE_URL}/productos/${itemKey}/${itemKey}-d.jpg`}
									alt={name}
									sx={styles.cardImageDesktop}
								/>
							</Linkable>
						</Box>
						<Box sx={styles.cardTitleRow}>
							<Checkbox
								checked={isChecked}
								onChange={() => onToggle(id)}
								sx={styles.cardCheckboxElement}
							/>
							<Linkable href={routeUrl} color="textPrimary" onClick={onTrackSelectItem}>
								<Typography
									className="buy-together__product-name"
									variant="bodyBaseline"
									sx={styles.cardNameDescriptionValue}
								>
									{name && parseHTML(textTransformToSequence(name))}
								</Typography>
							</Linkable>
						</Box>
					</>
				)}
				<Box sx={styles.cardDesktopFooter}>
					{!isMobile ? (
						<Box className="buy-together__product-price" sx={styles.cardControlContainer}>
							<HDMPriceDisplay price={productPrice} variant="md" />
						</Box>
					) : null}
					<NumberInput
						onChange={onQuantityChange}
						value={quantity}
						min={nominalQty}
						max={MAX_QTY}
						showControls
						disallowEmptyOnBlur
						thousandSeparator=""
						id={'card-item-quantity' + itemKey}
						isControlled
						sx={styles.cardNumberInput}
						className="buy-together__input-quantity"
					/>
				</Box>
			</CardContent>
		);
	}
);

export const ResponsiveLayout: React.FC<LayoutProps> = ({
	slides,
	checked,
	qty,
	onToggle,
	onQtySet,
	subtotal,
	availableQuantity = 0,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { BuyItTogether, AddAllProductsToCart } = useLocalization('MerchandisingAssociation');
	const priceProps = {
		offer: subtotal,
		list: 0,
		singleUnit: '',
	};

	const { partNumbers, quantities, manufacturers, attributes, products } = useMemo(() => {
		const filteredSlides = slides.filter((i) => checked[i.id]);

		return {
			partNumbers: filteredSlides.map((i) => i.partNumber),
			quantities: filteredSlides.map((i) => String(qty[i.id] ?? 1)),
			manufacturers: filteredSlides.map((i) => i.manufacturer),
			attributes: filteredSlides.map((i) => i.attributes),
			products: filteredSlides,
		};
	}, [slides, checked, qty]);

	const { orderItems } = useCart();

	const homeDeliveryDate = useHomeDeliveryDate(products?.[0], getStoreIdAndStateId().stLocId) ;
	const pickupDate =  usePickupDate(products?.[0], getStoreIdAndStateId().stLocId) ;
	const homeDeliveryDt =
		homeDeliveryDate?.status === 'with-date' || homeDeliveryDate?.status === 'same-day'
			? homeDeliveryDate?.deliveryDate
			: undefined;
	const pickupDt = pickupDate?.status === 'with-date' ? pickupDate?.deliveryDate : undefined;
	return (
		<Box id="buyTogetherCard" sx={styles.buyTogether} data-type="manual">
			<Box sx={getRootStyles(slides.length)}>
				{slides.map((product, idx) => (
					<>
						<Box key={product.id} sx={styles.cardLoopContainer}>
							<Card
								{...(idx === 0 && { id: 'currentProduct' })}
								variant="outlined"
								sx={idx === 0 ? cardFirstStyle : styles.cardRestStyle}
							>
								<ProductCard
									product={product}
									itemKey={product.partNumber}
									isChecked={checked[product.id] ?? true}
									quantity={qty[product.id] ?? 1}
									isMobile={isMobile}
									onToggle={onToggle}
									onQtySet={onQtySet}
									slidesQty={slides.length}
									orderItems={orderItems}
								/>
							</Card>
						</Box>
						{!isMobile && idx < slides.length - 1 ? (
							<Box sx={styles.cardPlusIconItems}>
								<AddIcon sx={styles.cardPlusIcon} />
							</Box>
						) : null}
					</>
				))}
			</Box>

			<Card variant="outlined" sx={cardTotalPriceContainerSx(slides.length)}>
				<CardContent sx={styles.cardBuyTogetherContainer}>
					<Typography variant="bodyBaseline" sx={cardBuyTogetherText}>
						{BuyItTogether.t()}
					</Typography>
					<Box id="buyTogetherPrice" sx={styles.cardBuyTogetherSubtotal}>
						<HDMPriceDisplay price={priceProps} variant="lg" />
					</Box>
					<AddProductCart
						id="buyTogetherAddToCart"
						partNumber={partNumbers}
						quantity={quantities}
						wording={AddAllProductsToCart.t()}
						manufacturer={manufacturers}
						attributes={attributes}
						disabled={!subtotal}
						fullWidth={false}
						sx={styles.cardSubtotalButtonActive}
						products={products}
						availableQuantity={availableQuantity}
						homeDeliveryDate={homeDeliveryDt}
						pickupDate={pickupDt}
					/>
				</CardContent>
			</Card>
		</Box>
	);
};
